import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { catchError, debounceTime, filter, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DeepPartial, Theme } from '../../shared-services/theme.types';
import { ThemeService } from '../../shared-services/theme.service';

type SmartTableColumnType =
  | 'text'
  | 'date'
  | 'number'
  | 'currency'
  | 'slider'
  | 'progress'
  | 'status'
  | 'dropdown'
  | 'action';
type SmartTableAlign = 'left' | 'center' | 'right';
type SmartTableScope = 'main' | 'popup';

interface SmartTableOption {
  label: string;
  value: any;
  disabled?: boolean;
}

interface SmartTablePopupConfig {
  title?: string;
  apiUrl?: string;
  method?: string;
  rows?: any[];
  rowsPath?: string;
  dataKey?: string;
  responsePath?: string;
  columns?: SmartTableColumn[];
  columnBindings?: Record<string, Partial<SmartTableColumn>>;
  headerValues?: Record<string, string>;
  tableHeight?: string | number;
  emptyStateMessage?: string;
  requestPayloadMode?: 'auto' | 'custom' | 'merge' | 'none';
  requestPayload?: any;
  queryParams?: Record<string, any>;
}

interface SmartTableRowPopupConfig extends SmartTablePopupConfig {
  enabled?: boolean;
  rowIndexes?: Array<number | 'n' | 'N'>;
  rowIds?: Array<string | number>;
  rowIdKey?: string;
  matchKey?: string;
  matchValue?: any;
  matchValues?: any[];
}

interface SmartTableSubmitActionConfig {
  label?: string;
  apiUrl?: string;
  method?: string;
  requestPayload?: any;
  queryParams?: Record<string, any>;
  successMessage?: string;
  errorMessage?: string;
  responseMessagePath?: string;
  align?: 'start' | 'center' | 'end';
}

interface SmartTableColumn {
  key: string;
  bindingKey?: string;
  header?: string;
  headerKey?: string;
  type?: SmartTableColumnType;
  editable?: boolean;
  width?: string;
  align?: SmartTableAlign;
  placeholder?: string;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
  minKey?: string;
  maxKey?: string;
  stepKey?: string;
  progressValueKey?: string;
  progressMax?: number;
  progressMaxKey?: string;
  progressColor?: string;
  progressColorKey?: string;
  statusColor?: string;
  statusColorKey?: string;
  compactFormat?: boolean;
  compactFormatKey?: string;
  compactUnit?: 'auto' | 'k' | 'm';
  compactUnitKey?: string;
  compactDecimals?: number;
  options?: any[];
  dropdownApiUrl?: string;
  dropdownMethod?: string;
  dropdownResponsePath?: string;
  dropdownLabelKey?: string;
  dropdownValueKey?: string;
  dropdownDisabledKey?: string;
  dropdownPlaceholder?: string;
  dropdownRequestPayload?: any;
  dropdownQueryParams?: Record<string, any>;
  actionLabel?: string;
  popup?: SmartTablePopupConfig;
}

interface SmartTableRecalculationConfig {
  enabled?: boolean;
  triggerAfterChanges?: number | string | string[] | {
    count?: number;
    columnKeys?: string[];
    keys?: string[];
    columns?: string[];
    bindingKeys?: string[];
  };
  watchColumns?: string[];
  apiUrl?: string;
  method?: string;
  responsePath?: string;
  syncMessage?: string;
}

interface SmartTableConfig {
  widgetId?: string;
  publishReadyState?: boolean;
  apiUrl?: string;
  method?: string;
  configUrl?: string;
  mockDataUrl?: string;
  columnBindings?: Record<string, Partial<SmartTableColumn>>;
  rows?: any[];
  /** Rows supplied by a page-level coordinated bootstrap; consumed only once. */
  initialRows?: any[];
  rowsPath?: string;
  dataKey?: string;
  columns?: SmartTableColumn[];
  /** Derive visible columns from returned rows when no columns are configured. */
  deriveColumnsFromRows?: boolean;
  /** Decimal precision assigned to numeric columns derived from response rows. */
  derivedNumericDecimals?: number;
  /** Derived columns that must remain text even when their values look numeric. */
  derivedNumericExcludeKeys?: string[];
  /** Keep identifier-shaped response fields (codes, part numbers, regions, IDs) as text. */
  preserveIdentifierLikeColumns?: boolean;
  /** Optional floor for each main-table column; excess width scrolls horizontally. */
  minimumColumnWidth?: number;
  /**
   * Preserve response values as supplied, except numeric values whose decimal
   * precision exceeds the threshold. Those values use `decimals` for display.
   */
  conditionalNumericRounding?: {
    decimalPlacesThreshold?: number;
    decimals?: number;
  };
  /** Publish distinct values from loaded rows for sibling actions such as export. */
  publishRowValuesAsFilter?: {
    key: string;
    columnKey: string;
  };
  /** Disable merging global request/query filters into this table's API call. */
  useGlobalFilters?: boolean;
  /** Let the latest global selections replace matching values from customPayload. */
  requestParamsOverrideCustomPayload?: boolean;
  /** Keep only configured columns whose keys are present in the row response. */
  showResponseColumnsOnly?: boolean;
  /** Optional per-table header colours (useful when the dashboard theme differs). */
  headerBackground?: string;
  headerTextColor?: string;
  headerValues?: Record<string, string>;
  tableHeight?: string | number;
  height?: string | number;
  emptyStateMessage?: string;
  rowPopup?: SmartTableRowPopupConfig;
  themeOverrides?: DeepPartial<Theme>;
  recalculation?: SmartTableRecalculationConfig;
  submitAction?: SmartTableSubmitActionConfig;
}

interface SmartTableViewState {
  scope: SmartTableScope;
  title: string;
  columns: SmartTableColumn[];
  rows: any[];
  loading: boolean;
  errorMessage: string;
  tableHeight: string;
  emptyStateMessage: string;
  headerValues?: Record<string, string>;
}

interface SmartTableTemplateContext {
  row?: any;
  column?: SmartTableColumn;
  event?: any;
}

@Component({
  selector: 'app-json-driven-smart-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './json-driven-smart-table.component.html',
  styleUrl: './json-driven-smart-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonDrivenSmartTableComponent implements OnInit, OnDestroy {
  private readonly minimumColumnWidth = 60;
  private readonly destroy$ = new Subject<void>();
  private readonly recalculationTrigger$ = new Subject<void>();
  private initialized = false;
  private initialRowsConsumed = false;
  private inputData: SmartTableConfig | null = null;
  private readonly dropdownOptionsCache = new Map<string, SmartTableOption[]>();
  private readonly dropdownLoadingKeys = new Set<string>();
  /** Latest global filter selections, applied to every row request. */
  private currentFilters: any = null;
  private readonly resizedColumnWidths = new Map<string, number>();
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private adjacentResizeStartWidth = 0;
  private adjacentResizingColumnKey: string | null = null;
  private resizingScope: SmartTableScope | null = null;
  resizingColumnKey: string | null = null;
  private readonly resizeMoveHandler = (event: MouseEvent): void => this.onColumnResize(event);
  private readonly resizeEndHandler = (): void => this.onColumnResizeEnd();

  @Input() set data(value: SmartTableConfig | null) {
    this.inputData = value;
    if (this.initialized) {
      this.initializeComponent();
    }
  }

  get data(): SmartTableConfig | null {
    return this.inputData;
  }

  loading = false;
  apiSyncing = false;
  errorMessage = '';
  syncMessage = '';
  tableHeight = 'var(--dvm-chart-height-lg, 58vh)';
  columns: SmartTableColumn[] = [];
  private configuredColumns: SmartTableColumn[] = [];
  rows: any[] = [];
  widgetConfig: SmartTableConfig | null = null;
  editedCellKeys = new Set<string>();
  lastEditedRow: any = null;

  popupOpen = false;
  popupTable: SmartTableViewState = this.createEmptyTableState('popup');

  submitInFlight = false;
  submitMessage = '';
  submitErrorMessage = '';

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    private hostElement: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.initialized = true;

    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyResolvedTheme();
      this.cdr.markForCheck();
    });

    this.responsiveLayoutService.reflow$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });

    // Filter widgets (dropdowns, date pickers, Reset) publish their selections
    // here. Without this the table only ever issued its initial request and a
    // dropdown selection never reached the API.
    this.globalFilterService.filter$.pipe(takeUntil(this.destroy$)).subscribe((filters: any) => {
      // `skipReload` emissions only exist so filter dropdowns can populate.
      if (filters?.skipReload) {
        return;
      }
      if (filters?.widgetId && filters.widgetId === this.widgetConfig?.widgetId) {
        return;
      }
      if (this.widgetConfig?.useGlobalFilters === false) {
        return;
      }

      this.currentFilters = filters;
      if (this.widgetConfig) {
        this.loadRows(this.widgetConfig);
      }
    });

    this.recalculationTrigger$
      .pipe(
        debounceTime(250),
        filter(() => this.shouldRunRecalculation()),
        switchMap(() => this.runRecalculationRequest()),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        if (response) {
          this.applyRecalculationResponse(response);
        }
      });

    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.publishReadyState(false);
    this.detachColumnResizeListeners();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get editedCellCount(): number {
    return this.editedCellKeys.size;
  }

  get editTriggerCount(): number {
    const triggerConfig = this.widgetConfig?.recalculation?.triggerAfterChanges;
    if (typeof triggerConfig === 'number') {
      return Math.max(1, Number(triggerConfig || 3));
    }

    const objectCount = Number(
      triggerConfig && typeof triggerConfig === 'object' && !Array.isArray(triggerConfig)
        ? triggerConfig.count
        : undefined,
    );
    if (Number.isFinite(objectCount) && objectCount > 0) {
      return Math.max(1, objectCount);
    }

    const configuredColumns = this.getTriggerColumnKeys();
    if (configuredColumns.length > 0) {
      return configuredColumns.length;
    }

    return 3;
  }

  get emptyStateMessage(): string {
    return this.widgetConfig?.emptyStateMessage || 'No table rows available.';
  }

  get hasSubmitAction(): boolean {
    return !!this.widgetConfig?.submitAction;
  }

  get submitButtonLabel(): string {
    return this.widgetConfig?.submitAction?.label || 'Submit';
  }

  get submitButtonAlignClass(): string {
    const align = this.widgetConfig?.submitAction?.align || 'end';
    return `align-${align}`;
  }

  getRowClasses(scope: SmartTableScope, row: any, rowIndex: number): Record<string, boolean> {
    return {
      'clickable-row': this.hasRowPopup(scope, row, rowIndex),
    };
  }

  trackByColumn = (_: number, column: SmartTableColumn): string => column.bindingKey || column.key;
  trackByRow = (_: number, row: any): string => this.getRowId(row, _);

  initializeComponent(): void {
    if (!this.initialized || !this.inputData) {
      return;
    }

    this.publishReadyState(false);
    this.loading = true;
    this.errorMessage = '';
    this.syncMessage = '';
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.submitInFlight = false;
    this.columns = [];
    this.configuredColumns = [];
    this.rows = [];
    this.popupOpen = false;
    this.popupTable = this.createEmptyTableState('popup');
    this.dropdownOptionsCache.clear();
    this.dropdownLoadingKeys.clear();
    this.editedCellKeys.clear();
    this.lastEditedRow = null;
    this.initialRowsConsumed = false;
    this.cdr.markForCheck();

    const hasInlineConfig =
      Array.isArray(this.inputData.columns) ||
      Array.isArray(this.inputData.rows) ||
      this.inputData.deriveColumnsFromRows === true;
    const configUrl = this.inputData.configUrl || (hasInlineConfig ? '' : this.inputData.apiUrl || '');

    if (!configUrl) {
      const mergedConfig = this.mergeWidgetConfigs(null, this.inputData);
      this.applyWidgetConfig(mergedConfig);
      return;
    }

    this.configService
      .getConfig(configUrl, this.inputData, undefined, this.resolveMethod(configUrl))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (remoteConfig) => this.applyWidgetConfig(this.mergeWidgetConfigs(remoteConfig, this.inputData!)),
        error: () => {
          this.loading = false;
          this.errorMessage = 'Unable to load table configuration.';
          this.cdr.markForCheck();
        },
      });
  }

  applyWidgetConfig(config: SmartTableConfig): void {
    this.widgetConfig = config;
    this.configuredColumns = this.normalizeColumns(config);
    this.columns = [...this.configuredColumns];
    this.tableHeight = this.resolveTableHeight(config);
    this.applyResolvedTheme();

    if (this.columns.length === 0 && !config.deriveColumnsFromRows) {
      this.loading = false;
      this.errorMessage = 'No table columns were configured. Define columns in dashboard JSON or table config.';
      this.cdr.markForCheck();
      return;
    }

    this.loadRows(config);
  }

  /**
   * Fetches the row set for `config` using the current global filter
   * selections. Called once when the widget config resolves and again on every
   * filter emission, so a dropdown selection re-issues the request.
   */
  private loadRows(config: SmartTableConfig): void {
    this.publishReadyState(false);
    if (!this.initialRowsConsumed && Array.isArray(config.initialRows)) {
      this.initialRowsConsumed = true;
      this.setRows(config.initialRows, config);
      this.loading = false;
      this.syncMessage = this.getPendingSyncMessage();
      this.cdr.markForCheck();
      return;
    }

    const inlineRows = this.extractRowsFromSource(config.rows, config);
    if (inlineRows.length > 0) {
      this.setRows(inlineRows, config);
      this.loading = false;
      this.syncMessage = this.getPendingSyncMessage();
      this.cdr.markForCheck();
      return;
    }

    const rowsUrl = config.apiUrl || config.mockDataUrl;
    if (!rowsUrl) {
      this.rows = [];
      this.loading = false;
      this.syncMessage = this.getPendingSyncMessage();
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    this.configService
      .getConfig(
        rowsUrl,
        config,
        this.resolveGlobalFilters(config),
        this.resolveMethod(rowsUrl, config.method),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.setRows(this.extractRowsFromSource(response, config), config);
          this.loading = false;
          this.syncMessage = this.getPendingSyncMessage();
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.errorMessage = 'Unable to load table rows.';
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Applies a row response and, when requested, removes configured columns the
   * service did not return. A key still counts as returned when every value is
   * null; presence, rather than truthiness, controls the mapping.
   */
  private setRows(rows: any[], config: SmartTableConfig): void {
    this.rows = this.cloneObject(rows);

    const publishedValues = config.publishRowValuesAsFilter;
    if (publishedValues?.key && publishedValues.columnKey) {
      const values = [...new Set(
        this.rows
          .map((row) => this.getValueByPath(row, publishedValues.columnKey))
          .filter((value) => value !== null && value !== undefined && value !== ''),
      )];
      this.globalFilterService.updateFilterSelection(
        publishedValues.key,
        values,
        false,
        config.widgetId,
        false,
      );
    }

    if (!this.configuredColumns.length && config.deriveColumnsFromRows) {
      this.configuredColumns = this.deriveColumnsFromRows(this.rows);
    }

    this.columns = config.showResponseColumnsOnly
      ? this.configuredColumns.filter((column) =>
          this.rows.some((row) => this.hasPath(row, column.key)),
        )
      : [...this.configuredColumns];
    this.publishReadyState(true);
  }

  private publishReadyState(ready: boolean): void {
    const config = this.inputData;
    if (config?.publishReadyState && config.widgetId) {
      this.globalFilterService.setWidgetReady(config.widgetId, ready);
    }
  }

  /** Uses the loaded selection for order, falling back to the first row's keys. */
  private deriveColumnsFromRows(rows: any[]): SmartTableColumn[] {
    const firstRow = rows.find((row) => row && typeof row === 'object' && !Array.isArray(row));
    if (!firstRow) return [];

    const filters = this.currentFilters || this.globalFilterService.getCurrentFilters();
    const rawSelection =
      filters?.requestParams?.['selected_columns'] ??
      filters?.queryParams?.['selected_columns'];
    const selectedKeys = (Array.isArray(rawSelection) ? rawSelection.flat(Infinity) : [rawSelection])
      .flatMap((value) => typeof value === 'string' ? value.split(/[\n,]/) : [value])
      .map((value) => String(value ?? '').trim())
      .filter((key) => key && Object.prototype.hasOwnProperty.call(firstRow, key));
    const keys = selectedKeys.length ? [...new Set(selectedKeys)] : Object.keys(firstRow);

    const configuredDecimals = Number(this.widgetConfig?.derivedNumericDecimals);
    const useDerivedNumericFormatting = Number.isFinite(configuredDecimals) && configuredDecimals >= 0;
    const excludedNumericKeys = new Set(
      (this.widgetConfig?.derivedNumericExcludeKeys || []).map((key) => String(key).trim()),
    );

    return keys.map((key) => {
      const date = this.isDateResponseColumn(rows, key);
      const numeric = useDerivedNumericFormatting
        && !date
        && !excludedNumericKeys.has(key)
        && !(this.widgetConfig?.preserveIdentifierLikeColumns && this.isIdentifierLikeKey(key))
        && this.isNumericResponseColumn(rows, key);
      return {
        key,
        header: this.humanize(key),
        type: date ? 'date' : numeric ? 'number' : 'text',
        ...(numeric ? { decimals: Math.floor(configuredDecimals) } : {}),
      } as SmartTableColumn;
    });
  }

  private isDateResponseColumn(rows: any[], key: string): boolean {
    const normalizedKey = String(key || '')
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_');
    if (!/(^|_)date($|_)/.test(normalizedKey)) {
      return false;
    }

    const values = rows
      .map((row) => this.getValueByPath(row, key))
      .filter((value) => value !== null && value !== undefined && value !== '' && value !== '-');

    return values.some((value) =>
      typeof value === 'string' && this.isIsoDateValue(value),
    );
  }

  private formatDateDisplayValue(value: any): string {
    const stringValue = String(value);
    const match = stringValue.match(/^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/i);
    return match?.[1] || stringValue;
  }

  private isIsoDateValue(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/i.test(value);
  }

  private isNumericResponseColumn(rows: any[], key: string): boolean {
    const values = rows
      .map((row) => this.getValueByPath(row, key))
      .filter((value) => value !== null && value !== undefined && value !== '' && value !== '-');

    return values.length > 0 && values.every((value) => {
      if (typeof value === 'number') {
        return Number.isFinite(value);
      }
      if (typeof value !== 'string') return false;
      const normalized = value.trim();
      return normalized !== ''
        && Number.isFinite(Number(normalized))
        // Leading zeroes carry meaning in identifiers (for example region "01").
        && !/^[+-]?0\d/.test(normalized);
    });
  }

  private isIdentifierLikeKey(key: string): boolean {
    const normalized = String(key || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    return /(^|_)(id|uuid|code|number|no|region)($|_)/.test(normalized);
  }

  private hasPath(source: any, path: string): boolean {
    if (!source || typeof source !== 'object' || !path) return false;
    const parts = path.split('.');
    let current = source;
    for (const part of parts) {
      if (!current || typeof current !== 'object' || !Object.prototype.hasOwnProperty.call(current, part)) {
        return false;
      }
      current = current[part];
    }
    return true;
  }

  /**
   * Current global selections in the shape `ConfigService.getConfig` expects.
   * Returns undefined while nothing is selected so the configured
   * `defaultRequestData` / `customPayload` is sent unchanged.
   */
  private resolveGlobalFilters(config?: SmartTableConfig): any {
    if (config?.useGlobalFilters === false) {
      return undefined;
    }
    const source = this.currentFilters || this.globalFilterService.getCurrentFilters();
    return this.buildRequestFilters(
      this.stripEmptySelections(source?.requestParams),
      this.stripEmptySelections(source?.queryParams),
    );
  }

  /** Drops keys the user cleared, so an empty dropdown does not narrow the query. */
  private stripEmptySelections(params: any): any {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
      return undefined;
    }

    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      cleaned[key] = value;
    }

    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  getHeaderLabel(column: SmartTableColumn, scope: SmartTableScope = 'main'): string {
    const headerKey = column.headerKey || '';
    const headerValues = scope === 'popup' ? this.popupTable.headerValues : this.widgetConfig?.headerValues;
    const configuredHeader = headerKey ? headerValues?.[headerKey] : '';
    return configuredHeader || column.header || this.humanize(column.key);
  }

  isSliderColumn(column: SmartTableColumn): boolean {
    return column.type === 'slider';
  }

  isProgressColumn(column: SmartTableColumn): boolean {
    return column.type === 'progress';
  }

  isStatusColumn(column: SmartTableColumn): boolean {
    return column.type === 'status';
  }

  isDropdownColumn(column: SmartTableColumn): boolean {
    return column.type === 'dropdown';
  }

  isActionColumn(column: SmartTableColumn): boolean {
    return column.type === 'action';
  }

  hasPopup(column: SmartTableColumn): boolean {
    return !!column.popup;
  }

  isEditableColumn(column: SmartTableColumn): boolean {
    return column.editable === true;
  }

  isPopupTriggerColumn(column: SmartTableColumn): boolean {
    return this.hasPopup(column);
  }

  getCellAlignment(column: SmartTableColumn): SmartTableAlign {
    return column.align || (this.isNumberLikeColumn(column) ? 'right' : 'left');
  }

  getCellClasses(column: SmartTableColumn): Record<string, boolean> {
    const alignment = this.getCellAlignment(column);
    return {
      'align-left': alignment === 'left',
      'align-center': alignment === 'center',
      'align-right': alignment === 'right',
      'editable-cell': this.isEditableColumn(column),
      'slider-column': this.isSliderColumn(column),
      'progress-column': this.isProgressColumn(column),
      'status-column': this.isStatusColumn(column),
      'dropdown-column': this.isDropdownColumn(column),
      'action-column': this.isActionColumn(column),
      'popup-trigger-column': this.isPopupTriggerColumn(column),
    };
  }

  getColumnWidth(column: SmartTableColumn, scope: SmartTableScope = 'main'): string | null {
    const resizedWidth = this.resizedColumnWidths.get(this.getColumnResizeKey(scope, column.key));
    return resizedWidth === undefined ? column.width || null : `${resizedWidth}px`;
  }

  getTableMinimumWidth(columns: SmartTableColumn[], scope: SmartTableScope = 'main'): number | null {
    if (scope !== 'main') return null;

    const configuredWidth = Number(this.widgetConfig?.minimumColumnWidth);
    if (!Number.isFinite(configuredWidth) || configuredWidth <= 0) return null;

    return Math.max(1180, columns.length * Math.max(this.minimumColumnWidth, configuredWidth));
  }

  onColumnResizeStart(event: MouseEvent, column: SmartTableColumn, scope: SmartTableScope): void {
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const headerCell = (event.currentTarget as HTMLElement | null)?.closest('th');
    const headerRow = headerCell?.parentElement;
    const headerCells = headerRow ? Array.from(headerRow.querySelectorAll('th')) : [];
    const headerIndex = headerCell ? headerCells.indexOf(headerCell) : -1;
    const adjacentHeaderCell = headerIndex >= 0 ? headerCells[headerIndex + 1] : null;
    const adjacentColumnKey = adjacentHeaderCell?.dataset['columnKey'];
    if (!headerCell || !adjacentHeaderCell || !adjacentColumnKey) return;

    // Lock every column to its rendered width before dragging. This prevents
    // percentage/auto columns from being redistributed by the browser while
    // the divider is moving.
    headerCells.forEach((cell) => {
      const key = cell.dataset['columnKey'];
      if (key) {
        this.resizedColumnWidths.set(
          this.getColumnResizeKey(scope, key),
          Math.round(cell.getBoundingClientRect().width),
        );
      }
    });

    this.resizingColumnKey = column.key;
    this.adjacentResizingColumnKey = adjacentColumnKey;
    this.resizingScope = scope;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = headerCell.getBoundingClientRect().width;
    this.adjacentResizeStartWidth = adjacentHeaderCell.getBoundingClientRect().width;
    this.attachColumnResizeListeners();
  }

  private onColumnResize(event: MouseEvent): void {
    if (!this.resizingColumnKey || !this.adjacentResizingColumnKey || !this.resizingScope) return;

    const requestedDelta = event.clientX - this.resizeStartX;
    const delta = Math.max(
      this.minimumColumnWidth - this.resizeStartWidth,
      Math.min(requestedDelta, this.adjacentResizeStartWidth - this.minimumColumnWidth),
    );
    const width = Math.round(this.resizeStartWidth + delta);
    const adjacentWidth = Math.round(this.adjacentResizeStartWidth - delta);
    this.resizedColumnWidths.set(
      this.getColumnResizeKey(this.resizingScope, this.resizingColumnKey),
      width,
    );
    this.resizedColumnWidths.set(
      this.getColumnResizeKey(this.resizingScope, this.adjacentResizingColumnKey),
      adjacentWidth,
    );
    this.cdr.markForCheck();
  }

  private onColumnResizeEnd(): void {
    this.resizingColumnKey = null;
    this.adjacentResizingColumnKey = null;
    this.resizingScope = null;
    this.detachColumnResizeListeners();
    this.cdr.markForCheck();
  }

  private getColumnResizeKey(scope: SmartTableScope, columnKey: string): string {
    return `${scope}:${columnKey}`;
  }

  private attachColumnResizeListeners(): void {
    if (typeof document === 'undefined') return;
    this.detachColumnResizeListeners();
    document.addEventListener('mousemove', this.resizeMoveHandler);
    document.addEventListener('mouseup', this.resizeEndHandler);
    document.body.classList.add('smart-table-column-resizing');
  }

  private detachColumnResizeListeners(): void {
    if (typeof document === 'undefined') return;
    document.removeEventListener('mousemove', this.resizeMoveHandler);
    document.removeEventListener('mouseup', this.resizeEndHandler);
    document.body.classList.remove('smart-table-column-resizing');
  }

  getDisplayValue(row: any, column: SmartTableColumn): string {
    const value = this.getValueByPath(row, column.key);
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    if (column.type === 'date') {
      return `${column.prefix || ''}${this.formatDateDisplayValue(value)}${column.suffix || ''}`;
    }

    const conditionalRounding = this.widgetConfig?.conditionalNumericRounding;
    if (conditionalRounding && (column.type === 'number' || this.isNumberLikeColumn(column))) {
      const threshold = this.resolveNonNegativeInteger(
        conditionalRounding.decimalPlacesThreshold,
        3,
      );
      const decimals = this.resolveNonNegativeInteger(conditionalRounding.decimals, 2);
      const numericValue = typeof value === 'number' ? value : Number(String(value).trim());
      const decimalPlaces = typeof value === 'string'
        ? this.countStringDecimalPlaces(value)
        : this.countDecimalPlaces(value);
      const displayValue = Number.isFinite(numericValue) && decimalPlaces > threshold
        ? this.trimTrailingZeros(numericValue.toFixed(decimals))
        : String(value);
      return `${column.prefix || ''}${displayValue}${column.suffix || ''}`;
    }

    if (column.type === 'currency') {
      return this.formatCurrencyValue(value, column, row);
    }

    if (column.type === 'number' || this.isNumberLikeColumn(column)) {
      return this.formatNumericValue(value, column, row);
    }

    return `${column.prefix || ''}${String(value)}${column.suffix || ''}`;
  }

  getInputValue(row: any, column: SmartTableColumn): string | number {
    const value = this.getValueByPath(row, column.key);
    if (value === undefined || value === null) {
      return '';
    }
    return value as string | number;
  }

  onInputChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    const input = event.target as HTMLInputElement;
    const nextValue = this.isNumberLikeColumn(column)
      ? this.normalizeNumericColumnValue(this.parseNumber(input.value), column)
      : input.value;
    row[column.key] = nextValue;
  }

  onInputCommit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    this.registerEdit(scope, rowIndex, row, column);
  }

  onSliderChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    const input = event.target as HTMLInputElement;
    row[column.key] = this.normalizeNumericColumnValue(this.parseNumber(input.value), column);
  }

  onSliderCommit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    this.registerEdit(scope, rowIndex, row, column);
  }

  onDropdownFocus(rowIndex: number, row: any, column: SmartTableColumn): void {
    this.ensureDropdownOptions(rowIndex, row, column);
  }

  onDropdownChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    const select = event.target as HTMLSelectElement;
    row[column.key] = this.resolveDropdownValue(select.value, rowIndex, row, column);
    this.registerEdit(scope, rowIndex, row, column);
  }

  getDropdownOptions(rowIndex: number, row: any, column: SmartTableColumn): SmartTableOption[] {
    const staticOptions = this.extractStaticOptions(column);
    if (staticOptions.length > 0) {
      return staticOptions;
    }

    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    return this.dropdownOptionsCache.get(cacheKey) || [];
  }

  isDropdownLoading(rowIndex: number, row: any, column: SmartTableColumn): boolean {
    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    return this.dropdownLoadingKeys.has(cacheKey);
  }

  getDropdownValue(row: any, column: SmartTableColumn): string {
    const value = this.getValueByPath(row, column.key);
    return value === undefined || value === null ? '' : String(value);
  }

  getActionLabel(row: any, column: SmartTableColumn): string {
    const configured = this.resolveDynamicValue(column.actionLabel, { row, column });
    if (configured !== undefined && configured !== null && configured !== '') {
      return String(configured);
    }

    if (this.isActionColumn(column)) {
      return 'Open';
    }

    return this.getDisplayValue(row, column);
  }

  hasRowPopup(scope: SmartTableScope, row: any, rowIndex: number): boolean {
    return !!this.resolveRowPopupConfig(scope, row, rowIndex);
  }

  onRowClick(scope: SmartTableScope, row: any, rowIndex: number, event: Event): void {
    const popupConfig = this.resolveRowPopupConfig(scope, row, rowIndex);
    if (!popupConfig || this.isInteractiveTarget(event.target)) {
      return;
    }

    this.openPopupFromConfig(row, undefined, popupConfig, event);
  }

  openPopup(row: any, column: SmartTableColumn, event?: Event): void {
    const popupConfig = column.popup;
    if (!popupConfig) {
      return;
    }

    this.openPopupFromConfig(row, column, popupConfig, event);
  }

  private openPopupFromConfig(
    row: any,
    column: SmartTableColumn | undefined,
    popupConfig: SmartTablePopupConfig,
    event?: Event,
  ): void {
    event?.stopPropagation();

    const popupColumns = this.normalizeColumns({
      columns: popupConfig.columns || [],
      columnBindings: popupConfig.columnBindings || {},
    });

    this.popupOpen = true;
    this.popupTable = {
      scope: 'popup',
      title: String(this.resolveDynamicValue(popupConfig.title || this.getHeaderLabel(column || { key: 'details' } as SmartTableColumn), { row, column }) || ''),
      columns: popupColumns,
      rows: [],
      loading: true,
      errorMessage: '',
      tableHeight: this.normalizeCssSize(popupConfig.tableHeight || '22rem'),
      emptyStateMessage: popupConfig.emptyStateMessage || 'No popup rows available.',
      headerValues: popupConfig.headerValues,
    };
    this.cdr.markForCheck();

    const inlineRows = this.extractRowsFromSource(popupConfig.rows, popupConfig);
    if (inlineRows.length > 0) {
      this.popupTable = {
        ...this.popupTable,
        rows: inlineRows,
        loading: false,
      };
      this.cdr.markForCheck();
      return;
    }

    const rawApiUrl = popupConfig.apiUrl || '';
    if (!rawApiUrl) {
      this.popupTable = {
        ...this.popupTable,
        loading: false,
        errorMessage: popupColumns.length === 0 ? 'No popup columns were configured.' : '',
      };
      this.cdr.markForCheck();
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(rawApiUrl, { row, column }) || '');
    const method = this.resolveMethod(apiUrl, popupConfig.method);
    const responsePath = String(this.resolveDynamicValue(popupConfig.responsePath, { row, column }) || '');
    const queryParams = this.resolveDynamicValue(popupConfig.queryParams, { row, column });
    const requestPayload = this.resolvePopupRequestPayload(apiUrl, popupConfig, row, column);
    const filters = this.buildRequestFilters(requestPayload, queryParams);

    this.configService
      .getConfig(apiUrl, popupConfig, filters, method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const source = responsePath ? this.getValueByPath(response, responsePath) : response;
          this.popupTable = {
            ...this.popupTable,
            rows: this.extractRowsFromSource(source, popupConfig),
            loading: false,
            errorMessage:
              popupColumns.length === 0
                ? 'No popup columns were configured.'
                : '',
          };
          this.cdr.markForCheck();
        },
        error: () => {
          this.popupTable = {
            ...this.popupTable,
            loading: false,
            errorMessage: 'Unable to load popup table rows.',
          };
          this.cdr.markForCheck();
        },
      });
  }

  closePopup(): void {
    this.popupOpen = false;
    this.popupTable = this.createEmptyTableState('popup');
    this.cdr.markForCheck();
  }

  submitTable(): void {
    const submitAction = this.widgetConfig?.submitAction;
    if (!submitAction?.apiUrl || this.submitInFlight) {
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(submitAction.apiUrl, {}) || '');
    const method = this.resolveMethod(apiUrl, submitAction.method);
    const requestPayload = this.resolveDynamicValue(submitAction.requestPayload, {});
    const queryParams = this.resolveDynamicValue(submitAction.queryParams, {});
    const filters = this.buildRequestFilters(
      requestPayload ?? {
        editedCellCount: this.editedCellCount,
        rows: this.cloneObject(this.rows),
      },
      queryParams,
    );

    this.submitInFlight = true;
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.cdr.markForCheck();

    this.configService
      .getConfig(apiUrl, submitAction, filters, method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const responseMessagePath = String(
            this.resolveDynamicValue(submitAction.responseMessagePath, {}) || '',
          );
          const messageFromResponse = responseMessagePath ? this.getValueByPath(response, responseMessagePath) : '';
          this.submitInFlight = false;
          this.submitMessage = String(messageFromResponse || submitAction.successMessage || 'Submitted successfully.');
          this.submitErrorMessage = '';
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitInFlight = false;
          this.submitMessage = '';
          this.submitErrorMessage = submitAction.errorMessage || 'Submit failed.';
          this.cdr.markForCheck();
        },
      });
  }

  getSliderMin(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.minKey, column.min, 0);
  }

  getSliderMax(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.maxKey, column.max, 100);
  }

  getSliderStep(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.stepKey, column.step, this.getDefaultSliderStep(column));
  }

  getProgressPercent(row: any, column: SmartTableColumn): number {
    const value = Number(this.resolveProgressValue(row, column));
    const max = Number(this.resolveProgressMax(row, column));
    if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(100, (value / max) * 100));
  }

  getProgressLabel(row: any, column: SmartTableColumn): string {
    const value = Number(this.resolveProgressValue(row, column));
    if (!Number.isFinite(value)) {
      return '0%';
    }
    return `${this.formatNumericValue(value, { ...column, type: 'number' }, undefined)}`;
  }

  getProgressColor(row: any, column: SmartTableColumn): string {
    const colorByKey = column.progressColorKey ? this.getValueByPath(row, column.progressColorKey) : '';
    return String(colorByKey || column.progressColor || '');
  }

  getStatusColor(row: any, column: SmartTableColumn): string {
    const keyColor = column.statusColorKey ? this.getValueByPath(row, column.statusColorKey) : '';
    return String(keyColor || column.statusColor || '');
  }

  getStatusLabel(row: any, column: SmartTableColumn): string {
    const value = this.getValueByPath(row, column.key);
    return value === undefined || value === null || value === '' ? 'Pending' : String(value);
  }

  private runRecalculationRequest() {
    const recalculation = this.widgetConfig?.recalculation;
    const apiUrl = recalculation?.apiUrl;
    if (!apiUrl || !this.widgetConfig) {
      return of<any>(null);
    }

    const method = this.resolveMethod(apiUrl, recalculation.method);

    this.apiSyncing = true;
    this.syncMessage = 'Running recalculation...';
    this.cdr.markForCheck();

    const requestPayload = {
      editedCellCount: this.editedCellCount,
      rows: this.cloneObject(this.rows),
    };

    return this.configService
      .getConfig(apiUrl, recalculation, { requestParams: requestPayload }, method)
      .pipe(
        catchError(() => {
          this.apiSyncing = false;
          this.syncMessage = 'Recalculation failed. API response could not be loaded.';
          this.cdr.markForCheck();
          return of<any>(null);
        }),
      );
  }

  private shouldRunRecalculation(): boolean {
    const recalculation = this.widgetConfig?.recalculation;
    return !!recalculation?.apiUrl && recalculation.enabled !== false && this.editedCellCount >= this.editTriggerCount;
  }

  private applyRecalculationResponse(response: any): void {
    const recalculation = this.widgetConfig?.recalculation;
    if (!recalculation) {
      return;
    }

    const payload = recalculation.responsePath ? this.getValueByPath(response, recalculation.responsePath) : response;
    const nextRows = this.extractRowsFromSource(payload, { rowsPath: undefined, dataKey: undefined });
    if (Array.isArray(nextRows) && nextRows.length > 0) {
      this.rows = nextRows;
    }

    this.apiSyncing = false;
    this.syncMessage = String(payload?.syncMessage || recalculation.syncMessage || `Synced after ${this.editedCellCount} edits`);
    this.cdr.markForCheck();
  }

  private registerEdit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    if (scope !== 'main') {
      return;
    }

    if (!this.shouldTrackColumnForRecalculation(column)) {
      return;
    }

    const trackedKey = column.bindingKey || column.key;
    const editKey = `${this.getRowId(row, rowIndex)}::${trackedKey}`;
    this.editedCellKeys.add(editKey);
    this.lastEditedRow = this.cloneObject(row);
    this.syncMessage = this.getPendingSyncMessage();
    this.recalculationTrigger$.next();
    this.cdr.markForCheck();
  }

  private shouldTrackColumnForRecalculation(column: SmartTableColumn): boolean {
    const trackedColumns = this.getTriggerColumnKeys();
    if (trackedColumns.length === 0) {
      return this.isEditableColumn(column) || this.isDropdownColumn(column);
    }

    const candidates = [column.bindingKey, column.key]
      .map((value) => String(value || '').trim())
      .filter((value) => !!value);

    return candidates.some((candidate) => trackedColumns.includes(candidate));
  }

  private getTriggerColumnKeys(): string[] {
    const recalculation = this.widgetConfig?.recalculation;
    const triggerConfig = recalculation?.triggerAfterChanges;
    if (triggerConfig) {
      if (typeof triggerConfig === 'string') {
        return [triggerConfig.trim()].filter((value) => !!value);
      }

      if (Array.isArray(triggerConfig)) {
        return triggerConfig.map((value) => String(value || '').trim()).filter((value) => !!value);
      }

      if (typeof triggerConfig === 'object') {
        const raw =
          triggerConfig.columnKeys ||
          triggerConfig.keys ||
          triggerConfig.columns ||
          triggerConfig.bindingKeys ||
          [];

        return Array.isArray(raw)
          ? raw.map((value) => String(value || '').trim()).filter((value) => !!value)
          : [];
      }
    }

    return Array.isArray(recalculation?.watchColumns)
      ? recalculation!.watchColumns!.map((value) => String(value || '').trim()).filter((value) => !!value)
      : [];
  }

  private getPendingSyncMessage(): string {
    if (this.editedCellCount >= this.editTriggerCount) {
      return this.apiSyncing ? 'Running recalculation...' : 'Edit threshold met. Recalculation will run automatically.';
    }

    const remaining = this.editTriggerCount - this.editedCellCount;
    return `${remaining} more editable ${remaining === 1 ? 'change' : 'changes'} needed before recalculation.`;
  }

  private normalizeColumns(config: Pick<SmartTableConfig | SmartTablePopupConfig, 'columns' | 'columnBindings'>): SmartTableColumn[] {
    const inputColumns = Array.isArray(this.inputData?.columns) && config === this.widgetConfig ? this.inputData!.columns! : [];
    const configColumns = Array.isArray(config.columns) ? config.columns : [];
    const sourceColumns = inputColumns.length > 0 ? inputColumns : configColumns;
    if (sourceColumns.length === 0) {
      return [];
    }

    const bindings = config.columnBindings || {};
    return sourceColumns.map((column) => this.resolveColumnBinding(column, bindings));
  }

  private resolveColumnBinding(
    column: SmartTableColumn,
    bindings: Record<string, Partial<SmartTableColumn>>,
  ): SmartTableColumn {
    const bindingKey = column.bindingKey || column.key;
    const binding = bindingKey ? bindings[bindingKey] : undefined;
    if (!binding) {
      return column;
    }

    const merged = {
      ...binding,
      ...column,
      key: column.key || binding.key || '',
      bindingKey,
    } as SmartTableColumn;

    if (!merged.key) {
      merged.key = bindingKey;
    }

    return merged;
  }

  private extractRowsFromSource(source: any, config: Pick<SmartTableConfig | SmartTablePopupConfig, 'rowsPath' | 'dataKey'>): any[] {
    if (Array.isArray(source)) {
      return this.cloneObject(source);
    }

    const candidatePaths = [config.rowsPath, config.dataKey].filter(Boolean) as string[];
    for (const path of candidatePaths) {
      const value = this.getValueByPath(source, path);
      if (Array.isArray(value)) {
        return this.cloneObject(value);
      }
    }

    const directRows = source?.tableRows || source?.rows || source?.data || source?.tableData;
    if (Array.isArray(directRows)) {
      return this.cloneObject(directRows);
    }

    return [];
  }

  private mergeWidgetConfigs(remoteConfig: any, inputConfig: SmartTableConfig): SmartTableConfig {
    const remote = remoteConfig && typeof remoteConfig === 'object' ? remoteConfig as SmartTableConfig : {};
    return {
      ...remote,
      ...inputConfig,
      columns: Array.isArray(inputConfig.columns) && inputConfig.columns.length > 0 ? inputConfig.columns : remote.columns,
      columnBindings: this.mergePlainObjects(remote.columnBindings, inputConfig.columnBindings),
      rows: Array.isArray(inputConfig.rows) && inputConfig.rows.length > 0 ? inputConfig.rows : remote.rows,
      headerValues: this.mergePlainObjects(remote.headerValues, inputConfig.headerValues),
      rowPopup: this.mergePlainObjects(remote.rowPopup, inputConfig.rowPopup),
      recalculation: this.mergePlainObjects(remote.recalculation, inputConfig.recalculation),
      submitAction: this.mergePlainObjects(remote.submitAction, inputConfig.submitAction),
      themeOverrides: this.mergePlainObjects(remote.themeOverrides, inputConfig.themeOverrides),
    };
  }

  private mergePlainObjects<T>(base: T | undefined, override: T | undefined): T | undefined {
    if (!base) {
      return override;
    }
    if (!override) {
      return base;
    }

    const merged = { ...(base as Record<string, any>) };
    Object.keys(override as Record<string, any>).forEach((key) => {
      const overrideValue = (override as Record<string, any>)[key];
      const baseValue = (base as Record<string, any>)[key];
      if (
        overrideValue &&
        typeof overrideValue === 'object' &&
        !Array.isArray(overrideValue) &&
        baseValue &&
        typeof baseValue === 'object' &&
        !Array.isArray(baseValue)
      ) {
        merged[key] = this.mergePlainObjects(baseValue, overrideValue);
      } else {
        merged[key] = overrideValue;
      }
    });
    return merged as T;
  }

  private applyResolvedTheme(): void {
    const overrides = this.widgetConfig?.themeOverrides || this.inputData?.themeOverrides;
    const resolvedTheme = this.themeService.resolve(overrides);
    this.themeService.applyThemeTokensTo(resolvedTheme, this.hostElement.nativeElement);
  }

  private resolveMethod(url: string, explicitMethod?: string): string {
    if (explicitMethod) {
      return explicitMethod;
    }
    return String(url).endsWith('.json') ? 'get' : 'post';
  }

  private resolveNumericSetting(row: any, key: string | undefined, fallback: number | undefined, defaultValue: number): number {
    const fromRow = key ? Number(this.getValueByPath(row, key)) : NaN;
    if (Number.isFinite(fromRow)) {
      return fromRow;
    }

    const fromConfig = Number(fallback);
    if (Number.isFinite(fromConfig)) {
      return fromConfig;
    }

    return defaultValue;
  }

  private getDefaultSliderStep(column: SmartTableColumn): number {
    const decimals = this.getColumnDecimals(column);
    if (decimals <= 0) {
      return 1;
    }

    return 1 / (10 ** decimals);
  }

  private resolveProgressValue(row: any, column: SmartTableColumn): number {
    const key = column.progressValueKey || column.key;
    return this.toFiniteNumber(this.getValueByPath(row, key));
  }

  private resolveProgressMax(row: any, column: SmartTableColumn): number {
    if (column.progressMaxKey) {
      const maxFromRow = Number(this.getValueByPath(row, column.progressMaxKey));
      if (Number.isFinite(maxFromRow) && maxFromRow > 0) {
        return maxFromRow;
      }
    }

    const maxFromConfig = Number(column.progressMax);
    return Number.isFinite(maxFromConfig) && maxFromConfig > 0 ? maxFromConfig : 100;
  }

  isNumberLikeColumn(column: SmartTableColumn): boolean {
    return column.type === 'number' || column.type === 'currency' || column.type === 'slider' || column.type === 'progress';
  }

  private getRowId(row: any, rowIndex: number): string {
    const explicitId = row?.id ?? row?._id ?? row?.rowId;
    return explicitId !== undefined && explicitId !== null ? String(explicitId) : `row-${rowIndex}`;
  }

  private formatCurrencyValue(value: any, column: SmartTableColumn, row?: any): string {
    const compact = this.formatCompactNumber(value, column, row);
    if (compact) {
      return `${column.prefix || '$'}${compact}${column.suffix || ''}`;
    }

    const decimals = this.getColumnDecimals(column, 2);
    const numericValue = this.toFiniteNumber(value);
    return `${column.prefix || '$'}${numericValue.toFixed(decimals)}${column.suffix || ''}`;
  }

  private formatNumericValue(value: any, column: SmartTableColumn, row?: any): string {
    const compact = this.formatCompactNumber(value, column, row);
    if (compact) {
      return `${column.prefix || ''}${compact}${column.suffix || ''}`;
    }

    const numericValue = this.toFiniteNumber(value);
    const decimals = this.getColumnDecimals(column, 0);
    return `${column.prefix || ''}${numericValue.toFixed(decimals)}${column.suffix || ''}`;
  }

  private formatCompactNumber(value: any, column: SmartTableColumn, row?: any): string | null {
    const numericValue = this.toFiniteNumber(value);
    const unit = this.resolveCompactUnit(column, numericValue, row);
    if (!unit) {
      return null;
    }

    const divisor = unit === 'm' ? 1_000_000 : 1_000;
    const scaled = numericValue / divisor;
    const configuredDecimals = Number(column.compactDecimals);
    const decimals = Number.isFinite(configuredDecimals) && configuredDecimals >= 0
      ? Math.floor(configuredDecimals)
      : 1;

    return `${this.trimTrailingZeros(scaled.toFixed(decimals))}${unit.toUpperCase()}`;
  }

  private resolveCompactUnit(column: SmartTableColumn, numericValue: number, row?: any): 'k' | 'm' | null {
    if (!this.shouldUseCompactFormat(column, row)) {
      return null;
    }

    const fromRow = column.compactUnitKey
      ? this.normalizeCompactUnit(this.getValueByPath(row, column.compactUnitKey))
      : null;
    const configuredUnit = this.normalizeCompactUnit(column.compactUnit);
    const unit = fromRow || configuredUnit || 'auto';

    if (unit === 'k' || unit === 'm') {
      return unit;
    }

    const absolute = Math.abs(numericValue);
    if (absolute >= 1_000_000) {
      return 'm';
    }

    if (absolute >= 1_000) {
      return 'k';
    }

    return null;
  }

  private shouldUseCompactFormat(column: SmartTableColumn, row?: any): boolean {
    const fromRow = column.compactFormatKey
      ? this.toBooleanFlag(this.getValueByPath(row, column.compactFormatKey))
      : null;
    if (fromRow !== null) {
      return fromRow;
    }

    if (typeof column.compactFormat === 'boolean') {
      return column.compactFormat;
    }

    return !!column.compactUnit || !!column.compactUnitKey;
  }

  private normalizeCompactUnit(value: unknown): 'auto' | 'k' | 'm' | null {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'k' || normalized === 'm' || normalized === 'auto') {
      return normalized;
    }

    return null;
  }

  private toBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
        return true;
      }

      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
        return false;
      }
    }

    return null;
  }

  private trimTrailingZeros(value: string): string {
    return value
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '');
  }

  private countDecimalPlaces(value: number): number {
    const [coefficient, exponentText] = String(value).toLowerCase().split('e');
    const coefficientDecimals = coefficient.includes('.')
      ? coefficient.length - coefficient.indexOf('.') - 1
      : 0;
    const exponent = Number(exponentText || 0);
    return Math.max(0, coefficientDecimals - exponent);
  }

  private countStringDecimalPlaces(value: string): number {
    const normalized = value.trim().toLowerCase();
    if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/.test(normalized)) return 0;
    const [coefficient, exponentText] = normalized.split('e');
    const coefficientDecimals = coefficient.includes('.')
      ? coefficient.length - coefficient.indexOf('.') - 1
      : 0;
    return Math.max(0, coefficientDecimals - Number(exponentText || 0));
  }

  private resolveNonNegativeInteger(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : fallback;
  }

  private normalizeNumericColumnValue(value: number, column: SmartTableColumn): number {
    const decimals = this.getColumnDecimals(column, 0);
    if (!Number.isFinite(value)) {
      return 0;
    }

    if (decimals <= 0) {
      return Math.round(value);
    }

    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }

  private parseNumber(value: string): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toFiniteNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getColumnDecimals(column: SmartTableColumn, fallback = 0): number {
    const raw = Number(column.decimals);
    if (!Number.isFinite(raw) || raw < 0) {
      return fallback;
    }

    return Math.floor(raw);
  }

  private normalizeCssSize(value: string | number): string {
    return typeof value === 'number' ? `${value}px` : String(value);
  }

  private resolveTableHeight(config: SmartTableConfig): string {
    const configured = config.tableHeight ?? config.height;
    if (configured !== undefined && configured !== null && configured !== '') {
      return this.normalizeCssSize(configured);
    }

    return this.responsiveLayoutService.resolveWidgetHeight(config);
  }

  private getValueByPath(source: any, path: string): any {
    if (!path) {
      return source;
    }

    return String(path)
      .split('.')
      .reduce((value: any, part: string) => (value == null ? undefined : value[part]), source);
  }

  private humanize(value: string): string {
    return String(value)
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (segment) => segment.toUpperCase());
  }

  private cloneObject<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map((item) => this.cloneObject(item)) as T;
    }

    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, any>).reduce((acc: any, key: string) => {
        acc[key] = this.cloneObject((value as Record<string, any>)[key]);
        return acc;
      }, {}) as T;
    }

    return value;
  }

  private ensureDropdownOptions(rowIndex: number, row: any, column: SmartTableColumn): void {
    const staticOptions = this.extractStaticOptions(column);
    if (staticOptions.length > 0 || !column.dropdownApiUrl) {
      return;
    }

    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    if (this.dropdownOptionsCache.has(cacheKey) || this.dropdownLoadingKeys.has(cacheKey)) {
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(column.dropdownApiUrl, { row, column }) || '');
    if (!apiUrl) {
      return;
    }

    const method = this.resolveMethod(apiUrl, column.dropdownMethod);
    const requestPayload = this.resolveDynamicValue(column.dropdownRequestPayload, { row, column });
    const queryParams = this.resolveDynamicValue(column.dropdownQueryParams, { row, column });
    const filters = this.buildRequestFilters(requestPayload, queryParams);
    const responsePath = String(this.resolveDynamicValue(column.dropdownResponsePath, { row, column }) || '');

    this.dropdownLoadingKeys.add(cacheKey);

    this.configService
      .getConfig(apiUrl, column, filters, method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const source = responsePath ? this.getValueByPath(response, responsePath) : response;
          this.dropdownOptionsCache.set(cacheKey, this.normalizeDropdownOptions(source, column));
          this.dropdownLoadingKeys.delete(cacheKey);
          this.cdr.markForCheck();
        },
        error: () => {
          this.dropdownLoadingKeys.delete(cacheKey);
          this.cdr.markForCheck();
        },
      });
  }

  private extractStaticOptions(column: SmartTableColumn): SmartTableOption[] {
    return this.normalizeDropdownOptions(column.options || [], column);
  }

  private normalizeDropdownOptions(source: any, column: SmartTableColumn): SmartTableOption[] {
    const candidate = Array.isArray(source)
      ? source
      : source?.options || source?.dropdownOptions || source?.items || source?.data || [];

    if (!Array.isArray(candidate)) {
      return [];
    }

    const labelKey = column.dropdownLabelKey || 'label';
    const valueKey = column.dropdownValueKey || 'value';
    const disabledKey = column.dropdownDisabledKey || 'disabled';

    return candidate.map((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const label = item[labelKey] ?? item[valueKey] ?? item['name'] ?? item['title'] ?? '';
        const value = item[valueKey] ?? item[labelKey] ?? label;
        return {
          label: String(label),
          value,
          disabled: Boolean(item[disabledKey]),
        };
      }

      return {
        label: String(item),
        value: item,
        disabled: false,
      };
    });
  }

  private buildDropdownCacheKey(rowIndex: number, row: any, column: SmartTableColumn): string {
    const rowKey = column.dropdownRequestPayload || column.dropdownQueryParams ? this.getRowId(row, rowIndex) : 'shared';
    return `${column.bindingKey || column.key}::${rowKey}`;
  }

  private resolveDropdownValue(rawValue: string, rowIndex: number, row: any, column: SmartTableColumn): any {
    const options = this.getDropdownOptions(rowIndex, row, column);
    const matched = options.find((option) => String(option.value) === rawValue);
    return matched ? matched.value : rawValue;
  }

  private buildRequestFilters(requestPayload: any, queryParams: any): any {
    const filters: any = {};

    if (requestPayload !== undefined && requestPayload !== null && requestPayload !== '') {
      filters.requestParams = requestPayload;
    }

    if (queryParams && typeof queryParams === 'object' && !Array.isArray(queryParams)) {
      filters.queryParams = queryParams;
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  private resolvePopupRequestPayload(
    apiUrl: string,
    popupConfig: SmartTablePopupConfig,
    row: any,
    column?: SmartTableColumn,
  ): any {
    const configuredPayload = this.resolveDynamicValue(popupConfig.requestPayload, { row, column });
    const defaultPayload = this.getDefaultPopupRequestPayload(apiUrl, row, column);
    const mode = String(popupConfig.requestPayloadMode || '').trim().toLowerCase();

    if (mode === 'none') {
      return undefined;
    }

    if (mode === 'custom') {
      return configuredPayload;
    }

    if (mode === 'merge') {
      return this.mergePayloadValues(defaultPayload, configuredPayload);
    }

    if (mode === 'auto') {
      return defaultPayload;
    }

    return configuredPayload ?? defaultPayload;
  }

  private getDefaultPopupRequestPayload(apiUrl: string, row: any, column?: SmartTableColumn): any {
    if (!apiUrl || this.isLocalAssetUrl(apiUrl)) {
      return undefined;
    }

    const clickedRow = this.cloneObject(row);
    const payload: Record<string, any> = {
      ...clickedRow,
      clickedRow,
      clickedRowId: this.getRowId(row, 0),
    };

    if (column?.key) {
      payload['clickedColumnKey'] = column.key;
      payload['clickedColumnValue'] = this.getValueByPath(row, column.key);
      payload[column.key] = this.getValueByPath(row, column.key);
    }

    return payload;
  }

  private mergePayloadValues(base: any, override: any): any {
    if (base == null) {
      return override;
    }
    if (override == null) {
      return base;
    }

    if (
      base &&
      typeof base === 'object' &&
      !Array.isArray(base) &&
      override &&
      typeof override === 'object' &&
      !Array.isArray(override)
    ) {
      return {
        ...base,
        ...override,
      };
    }

    return override;
  }

  private createEmptyTableState(scope: SmartTableScope): SmartTableViewState {
    return {
      scope,
      title: '',
      columns: [],
      rows: [],
      loading: false,
      errorMessage: '',
      tableHeight: scope === 'main' ? this.tableHeight : '22rem',
      emptyStateMessage: scope === 'main' ? this.emptyStateMessage : 'No popup rows available.',
      headerValues: undefined,
    };
  }

  private resolveRowPopupConfig(scope: SmartTableScope, row: any, rowIndex: number): SmartTableRowPopupConfig | null {
    if (scope !== 'main') {
      return null;
    }

    const config = this.widgetConfig?.rowPopup;
    if (!config || config.enabled === false) {
      return null;
    }

    const rowIndexes = Array.isArray(config.rowIndexes) ? config.rowIndexes : [];
    if (this.matchesRowIndexSelector(rowIndexes, rowIndex)) {
      return config;
    }

    const rowIds = Array.isArray(config.rowIds) ? config.rowIds.map((value) => String(value)) : [];
    if (rowIds.length > 0) {
      const rowIdKey = config.rowIdKey || 'id';
      const candidateId = this.getValueByPath(row, rowIdKey);
      if (candidateId !== undefined && candidateId !== null && rowIds.includes(String(candidateId))) {
        return config;
      }
    }

    if (config.matchKey) {
      const rowValue = this.getValueByPath(row, config.matchKey);
      if (Array.isArray(config.matchValues) && config.matchValues.some((value) => String(value) === String(rowValue))) {
        return config;
      }
      if (config.matchValue !== undefined && String(config.matchValue) === String(rowValue)) {
        return config;
      }
    }

    return rowIndexes.length === 0 && rowIds.length === 0 && !config.matchKey ? config : null;
  }

  private matchesRowIndexSelector(indexes: Array<number | 'n' | 'N'>, rowIndex: number): boolean {
    if (indexes.length === 0) {
      return false;
    }

    const normalized = indexes
      .map((value) => this.normalizeRowIndexValue(value))
      .filter((value): value is number => Number.isFinite(value));

    if (normalized.includes(rowIndex)) {
      return true;
    }

    if (normalized.length === 2) {
      const [start, end] = normalized;
      const rangeStart = Math.min(start, end);
      const rangeEnd = Math.max(start, end);
      return rowIndex >= rangeStart && rowIndex <= rangeEnd;
    }

    return false;
  }

  private normalizeRowIndexValue(value: number | 'n' | 'N'): number {
    if (value === 'n' || value === 'N') {
      return Math.max(0, this.rows.length - 1);
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : NaN;
  }

  private isLocalAssetUrl(url: string): boolean {
    return url.startsWith('assets') || url.startsWith('/assets');
  }

  private isInteractiveTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return !!target.closest('button, input, select, textarea, a, label');
  }

  private resolveDynamicValue(value: any, context: SmartTableTemplateContext): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.resolveDynamicValue(item, context));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc: Record<string, any>, key: string) => {
        acc[key] = this.resolveDynamicValue(value[key], context);
        return acc;
      }, {});
    }

    if (typeof value !== 'string') {
      return value;
    }

    if (value.startsWith('$row.')) {
      return this.getValueByPath(context.row, value.slice(5));
    }
    if (value === '$row') {
      return context.row;
    }
    if (value.startsWith('$column.')) {
      return this.getValueByPath(context.column, value.slice(8));
    }
    if (value === '$column') {
      return context.column;
    }
    if (value === '$rows') {
      return this.cloneObject(this.rows);
    }
    if (value === '$editedRow') {
      return this.cloneObject(this.lastEditedRow);
    }
    if (value === '$editedCellCount') {
      return this.editedCellCount;
    }

    const exactMatch = value.match(/^\{\{\s*([^}]+)\s*\}\}$/);
    if (exactMatch) {
      return this.resolveDynamicToken(exactMatch[1], context);
    }

    return value.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_match, token: string) => {
      const resolved = this.resolveDynamicToken(token, context);
      return resolved === undefined || resolved === null ? '' : String(resolved);
    });
  }

  private resolveDynamicToken(token: string, context: SmartTableTemplateContext): any {
    const normalized = token.trim();

    if (normalized === 'row') {
      return context.row;
    }
    if (normalized.startsWith('row.')) {
      return this.getValueByPath(context.row, normalized.slice(4));
    }
    if (normalized === 'column') {
      return context.column;
    }
    if (normalized.startsWith('column.')) {
      return this.getValueByPath(context.column, normalized.slice(7));
    }
    if (normalized === 'rows') {
      return this.cloneObject(this.rows);
    }
    if (normalized === 'editedRow') {
      return this.cloneObject(this.lastEditedRow);
    }
    if (normalized.startsWith('editedRow.')) {
      return this.getValueByPath(this.lastEditedRow, normalized.slice(10));
    }
    if (normalized === 'editedCellCount') {
      return this.editedCellCount;
    }
    if (normalized === 'widget') {
      return this.widgetConfig;
    }
    if (normalized.startsWith('widget.')) {
      return this.getValueByPath(this.widgetConfig, normalized.slice(7));
    }
    if (normalized === 'event') {
      return context.event;
    }
    if (normalized.startsWith('event.')) {
      return this.getValueByPath(context.event, normalized.slice(6));
    }

    return '';
  }
}
